/* eslint-disable react-hooks/set-state-in-effect */
import { useState, useEffect, useCallback, useMemo } from "react";
import { literals } from "@/constants/literals";
import { useNavigate } from "react-router-dom";
import type {
  NewReceptionData,
  TemporalReceptionOrderData,
} from "@/types/types.d";
import useReceptionDataStore from "@/store/useReceptionData.store";
import ReceptionOrder from "./components/ReceptionOrder";
import Packaging from "./components/Packaging";
import CreateSectionsButtons from "./components/CreateSectionsButtons";
import ReceptionOrderHeader from "../components/ReceptionOrderHeader";
import { useLoader } from "@/store/useLoader";
import { askOrderExist, getInDataAndCustomer } from "@/lib/services/order";
import { appToast } from "@/lib/toast";
import ConfirmcreateReception from "./components/ConfirmCreateReception";

const initial_status = {
  step: "",
  customerOrderNoserv: "",
  serialIn: "",
  orderIn: "",
};

type DataSection = "order" | "packaging";

const CreateReception = () => {
  const [receptionGeneralData, setReceptionGeneralData] =
    useState<TemporalReceptionOrderData>(initial_status);
  const [exitFromIn, setExistFromIn] = useState(false);
  const [dataSection, setDataSection] = useState<DataSection>("order");
  const [lastOrderCustomerNoServ, setLastOrderCustomerNoServ] =
    useState<string>("");
  const [confirmInModal, setConfirmInModal] = useState("");
  const [needChangeData, setNeedChangeData] = useState(false);

  const { receptionData, updateReceptionData, clearReceptionGroupData } =
    useReceptionDataStore();
  const navigate = useNavigate();
  const { upLoader, downLoader } = useLoader();

  useEffect(() => {
    if (
      receptionData.customerOrderNoserv &&
      receptionData.customer &&
      receptionData.step === literals.inNoServ
    ) {
      const newInitialStatus = {
        step: literals.outNoServ,
        customerOrderNoserv: receptionData.customerOrderNoserv,
        serialIn: receptionData.serialIn,
        orderIn: receptionData.orderIn,
        ownPackaging: receptionData.ownPackaging,
        correctPackaging: receptionData.correctPackaging,
        damagedPackaging: receptionData.damagedPackaging,
        customer: receptionData.customer,
        originalCustomer: receptionData.customer,
      };
      setLastOrderCustomerNoServ(receptionData.customerOrderNoserv);
      setReceptionGeneralData(newInitialStatus);
      setExistFromIn(true);
      clearReceptionGroupData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isOut = useMemo(
    () => receptionGeneralData.step === literals.outNoServ,
    [receptionGeneralData.step],
  );

  const changeReceptionGeneralData = useCallback(
    (property: string, value: string | undefined) => {
      if (property === "step" && lastOrderCustomerNoServ.length > 0) {
        setLastOrderCustomerNoServ("");
      }
      setReceptionGeneralData((previousState) => {
        if (property === "step") {
          return { step: value as string };
        }
        const newReceptionData = { ...previousState };
        newReceptionData[property as Exclude<keyof NewReceptionData, "step">] =
          value;
        if (needChangeData) setNeedChangeData(false);
        if (property === "orderNumber" || property === "customer") {
          if (
            newReceptionData.orderNumber &&
            newReceptionData.ownPackaging === true &&
            newReceptionData.customer &&
            newReceptionData.customer.length >= 3 &&
            newReceptionData.customer.length <= 6
          ) {
            newReceptionData.packagingReference = `${newReceptionData.customer}_${value}`;
          } else if ("packagingReference" in newReceptionData) {
            delete newReceptionData.packagingReference;
          }
        }
        return newReceptionData;
      });
    },
    [needChangeData, lastOrderCustomerNoServ],
  );

  const changeOwnPackaging = useCallback((value?: boolean) => {
    setReceptionGeneralData((previousState: TemporalReceptionOrderData) => {
      const newReceptionData = { ...previousState };
      newReceptionData.ownPackaging =
        value !== undefined ? value : !previousState.ownPackaging;
      return newReceptionData;
    });
  }, []);

  const changeCorrectPackaging = useCallback((value?: boolean) => {
    setReceptionGeneralData((previousState: TemporalReceptionOrderData) => {
      const newReceptionData = { ...previousState };
      newReceptionData.correctPackaging =
        value !== undefined ? value : !previousState.correctPackaging;
      return newReceptionData;
    });
  }, []);

  const changeDamagedPackaging = useCallback((value?: boolean) => {
    setReceptionGeneralData((previousState: TemporalReceptionOrderData) => {
      const newReceptionData = { ...previousState };
      newReceptionData.damagedPackaging =
        value !== undefined ? value : !previousState.damagedPackaging;
      return newReceptionData;
    });
  }, []);

  const correctCustomerData = useMemo(
    () =>
      !needChangeData &&
      receptionGeneralData.customerOrderNoserv &&
      receptionGeneralData.customer &&
      receptionGeneralData.customer.length >= 3 &&
      receptionGeneralData.customer.length <= 6
        ? true
        : false,
    [
      receptionGeneralData.customerOrderNoserv,
      receptionGeneralData.customer,
      needChangeData,
    ],
  );
  const canChangeToPackage = useMemo(
    () => (!isOut && correctCustomerData ? true : false),
    [isOut, correctCustomerData],
  );

  const searchDataInAndCustomer = useCallback(async () => {
    if (receptionGeneralData.customerOrderNoserv) {
      if (needChangeData) setNeedChangeData(false);
      try {
        upLoader();
        const askedData = await getInDataAndCustomer(
          receptionGeneralData.customerOrderNoserv,
        );
        setReceptionGeneralData((previousState) => {
          const newReceptionData = { ...previousState };
          newReceptionData.orderIn = askedData.orderIn || "";
          newReceptionData.serialIn = askedData.serialIn || "";
          newReceptionData.ownPackaging =
            typeof askedData.ownPackaging === "boolean"
              ? askedData.ownPackaging
              : false;
          newReceptionData.correctPackaging =
            typeof askedData.correctPackaging === "boolean"
              ? askedData.correctPackaging
              : false;
          newReceptionData.damagedPackaging =
            typeof askedData.damagedPackaging === "boolean"
              ? askedData.damagedPackaging
              : false;
          if (askedData.customer) {
            newReceptionData.customer = askedData.customer;
            newReceptionData.originalCustomer = askedData.customer;
          } else {
            newReceptionData.customer = "";
            if (newReceptionData.originalCustomer)
              delete newReceptionData.originalCustomer;
          }
          if (
            previousState.orderNumber &&
            askedData.ownPackaging &&
            askedData.customer
          ) {
            newReceptionData.packagingReference = `${askedData.customer}_${askedData.orderNumber}`;
          } else if ("packagingReference" in previousState) {
            delete newReceptionData.packagingReference;
          }
          return newReceptionData;
        });
        setLastOrderCustomerNoServ(receptionGeneralData.customerOrderNoserv);
        downLoader();
      } catch (error) {
        if (
          error === literals.errorNotFound ||
          (error instanceof Error && error.message === literals.notFound)
        ) {
          appToast.warning(literals.notFoundData, literals.noRoOrder);
          setReceptionGeneralData((previousState) => {
            const newReceptionData = { ...previousState };
            newReceptionData.orderIn = "";
            newReceptionData.serialIn = "";
            newReceptionData.ownPackaging = false;
            newReceptionData.correctPackaging = false;
            newReceptionData.damagedPackaging = false;
            newReceptionData.customer = "";
            if ("packagingReference" in previousState) {
              delete newReceptionData.packagingReference;
            }
            return newReceptionData;
          });
        } else {
          appToast.error(literals.aProblem, literals.getInfoError);
        }
        downLoader();
      }
      setLastOrderCustomerNoServ(receptionGeneralData.customerOrderNoserv);
    }
  }, [
    receptionGeneralData.customerOrderNoserv,
    upLoader,
    downLoader,
    needChangeData,
  ]);

  const isValidData = useMemo(() => {
    const {
      customerOrderNoserv,
      serialIn,
      orderIn,
      ownPackaging,
      correctPackaging,
      damagedPackaging,
      orderNumber,
    } = receptionGeneralData;
    return (
      (dataSection === "order" &&
        isOut &&
        !needChangeData &&
        correctCustomerData &&
        lastOrderCustomerNoServ === customerOrderNoserv &&
        serialIn &&
        orderIn &&
        orderNumber &&
        orderNumber.length >= 10 &&
        orderNumber.length <= 12) ||
      (dataSection === "packaging" &&
        !isOut &&
        !needChangeData &&
        canChangeToPackage &&
        ownPackaging !== undefined &&
        correctPackaging !== undefined &&
        damagedPackaging !== undefined)
    );
  }, [
    canChangeToPackage,
    correctCustomerData,
    dataSection,
    isOut,
    lastOrderCustomerNoServ,
    needChangeData,
    receptionGeneralData,
  ]);

  const confirmCanContinue = useCallback(async () => {
    const { orderNumber: sapWorkOrder, ...otherRecptionGeneralData } =
      receptionGeneralData;
    const params = {
      area: literals.noServCell,
      sapWorkOrder,
      ...otherRecptionGeneralData,
    };
    try {
      upLoader();
      const orderInfo = await askOrderExist(params);
      const { restrict, warning, message } = orderInfo;
      downLoader();
      if (restrict) {
        setNeedChangeData(true);
        appToast.warning(literals.noValidData, message);
        return false;
      }
      if (warning && receptionGeneralData.step === literals.outNoServ) {
        setConfirmInModal(message as string);
        return false;
      }
      return true;
    } catch (error) {
      downLoader();
      if (error) {
        appToast.error(literals.aProblem, literals.getNecesaryInfoError);
        return false;
      }
      return false;
    }
  }, [downLoader, upLoader, receptionGeneralData]);

  const changeDataSection = useCallback(async () => {
    if (canChangeToPackage) {
      let canContinue = true;
      if (dataSection === "order" && !isOut) {
        canContinue = await confirmCanContinue();
      }
      if (canContinue)
        setDataSection((previousSection) =>
          previousSection === "order" ? "packaging" : "order",
        );
    }
  }, [confirmCanContinue, isOut, dataSection, canChangeToPackage]);

  const continueCapture = useCallback(
    async (
      event?: React.BaseSyntheticEvent<
        Event,
        EventTarget & HTMLFormElement,
        EventTarget
      >,
    ) => {
      if (event) event.preventDefault();
      if (!isValidData) return;
      if (
        dataSection === "order" &&
        isOut &&
        receptionGeneralData.ownPackaging &&
        receptionGeneralData.customer
      ) {
        if (confirmInModal === "") {
          const canContinue = await confirmCanContinue();
          if (!canContinue) return;
        }
        const updatedReceptionGeneralData = {
          ...receptionGeneralData,
          packagingReference: `${receptionGeneralData.customer}_${receptionGeneralData.orderNumber}`,
        };
        updateReceptionData(updatedReceptionGeneralData as NewReceptionData);
        navigate("new-group");
        return;
      }
      updateReceptionData(receptionGeneralData as NewReceptionData);
      navigate("new-group");
    },
    [
      dataSection,
      isOut,
      navigate,
      receptionGeneralData,
      updateReceptionData,
      confirmCanContinue,
      confirmInModal,
      isValidData,
    ],
  );

  return (
    <div className="min-h-fulldpx flex flex-col justify-between bg-ib-pt-color-white">
      <ReceptionOrderHeader newOrderText />
      <form
        onSubmit={continueCapture}
        className="flex flex-col flex-1 justify-between md:justify-start"
      >
        {dataSection === "order" ? (
          <ReceptionOrder
            receptionGeneralData={receptionGeneralData}
            changeReceptionGeneralData={changeReceptionGeneralData}
            exitFromIn={exitFromIn}
            isOut={isOut}
            searchDataInAndCustomer={searchDataInAndCustomer}
            lastOrderCustomerNoServ={lastOrderCustomerNoServ}
          />
        ) : (
          <Packaging
            ownPackaging={receptionGeneralData.ownPackaging}
            changeOwnPackaging={changeOwnPackaging}
            correctPackaging={receptionGeneralData.correctPackaging}
            changeCorrectPackaging={changeCorrectPackaging}
            damagedPackaging={receptionGeneralData.damagedPackaging}
            changeDamagedPackaging={changeDamagedPackaging}
          />
        )}
        <ConfirmcreateReception
          continueCapture={continueCapture}
          closeModal={() => setConfirmInModal("")}
          message={confirmInModal}
        />
        <CreateSectionsButtons
          dataSection={dataSection}
          isOut={isOut}
          isValidData={isValidData}
          canChangeToPackage={canChangeToPackage}
          changeDataSection={changeDataSection}
          continueCapture={continueCapture}
        />
      </form>
    </div>
  );
};

export default CreateReception;
